/**
 * A button at the end of the view widget's top row that opens a small card
 * beneath it: the settings gear, the view menu's eye, and the export menu.
 * Dismissed by a press anywhere outside it, Escape, the button again, or a
 * menu item that has done its job.
 */

import { css } from '@emotion/react';
import { createContext, type ReactNode, useContext } from 'react';
import { bone, fBody, ink, menuShadow, midtone, monoLabel, paper, white } from '../styles';
import { usePopupOpenState } from './usePopupOpenState';

const HOST_STYLE = css`
  label: OVERLAY_MENU_HOST_STYLE;
  position: relative; display: inline-flex;
`;

const BUTTON_STYLE = css`
  label: OVERLAY_MENU_BUTTON_STYLE;
  display: inline-flex; align-items: center; justify-content: center;
  width: 34px; padding: 0; border: 0; border-left: 1px solid ${ink};
  background: transparent; color: ${midtone}; cursor: pointer;
  transition: background .12s, color .12s;
  &:hover { color: ${ink}; background: ${paper}; }
  svg { width: 14px; height: 14px; display: block; }
`;

const BUTTON_OPEN_STYLE = css`
  label: OVERLAY_MENU_BUTTON_OPEN_STYLE;
  background: ${ink}; color: ${paper};
  &:hover { color: ${paper}; background: ${ink}; }
`;

const CARD_STYLE = css`
  label: OVERLAY_MENU_CARD_STYLE;
  position: absolute; top: calc(100% + 6px); right: -1px; z-index: 42;
  width: 224px; box-sizing: border-box; padding: 12px 14px;
  background: ${white}; border: 1px solid ${ink};
  box-shadow: ${menuShadow};
  font-family: ${fBody}; font-size: 12px; line-height: 1.4; color: ${ink};
  text-align: left; text-transform: none; letter-spacing: normal;
`;

/** A section heading; every section after the first opens with a hairline across the card. */
const CARD_HEAD_STYLE = css`
  label: OVERLAY_MENU_CARD_HEAD_STYLE;
  ${monoLabel}
  font-size: 10px; color: ${midtone};
  margin-bottom: 6px;
  * + &, [role='group'] + [role='group'] > & {
    margin: 8px -14px 6px; padding: 10px 14px 0; border-top: 1px solid ${bone};
  }
`;

/** One choice in a card: a radio or checkbox with its label, and an optional example at the right. */
export const MENU_CHOICE_STYLE = css`
  label: OVERLAY_MENU_CHOICE_STYLE;
  display: flex; align-items: center; gap: 8px; padding: 5px 0; cursor: pointer;
  input { margin: 0; accent-color: ${ink}; }
  input:disabled, input:disabled + * { cursor: default; }
  small { margin-left: auto; color: ${midtone}; font-size: 11px; font-variant-numeric: tabular-nums; }
`;

/** An action in a card: a full-width row that highlights on hover, like a menu entry. */
const MENU_ITEM_STYLE = css`
  label: OVERLAY_MENU_ITEM_STYLE;
  display: block; box-sizing: border-box; width: calc(100% + 28px); margin: 0 -14px; padding: 5px 14px;
  border: 0; background: transparent; color: ${ink}; cursor: pointer;
  font: inherit; text-align: left;
  &:hover:not(:disabled) { background: ${paper}; }
  &:disabled { color: ${midtone}; cursor: default; }
`;

/** A heading over a group of choices. */
export function MenuHead({ children }: { children: ReactNode }): React.JSX.Element {
  return <div css={CARD_HEAD_STYLE}>{children}</div>;
}

/** Closes the card the caller sits in. */
const CloseOverlayMenuContext = createContext<() => void>(() => {});

export interface MenuItemProps {
  onSelect: () => void;
  /** Keep the card open after the action, to show its result in the card. */
  keepOpen?: boolean;
  disabled?: boolean;
  title?: string;
  children: ReactNode;
}

/** An action in an overlay menu's card. It closes the card once it has run, unless `keepOpen`. */
export function MenuItem({ onSelect, keepOpen = false, disabled = false, title, children }: MenuItemProps): React.JSX.Element {
  const closeMenu = useContext(CloseOverlayMenuContext);
  return (
    <button
      type='button'
      css={MENU_ITEM_STYLE}
      disabled={disabled}
      title={title}
      onClick={() => {
        onSelect();
        if (!keepOpen) closeMenu();
      }}
    >
      {children}
    </button>
  );
}

export interface OverlayMenuProps {
  label: string;
  icon: ReactNode;
  /** The button's test id; the card's is the same with `-card` in place of `-button`. */
  testId: string;
  children: ReactNode;
}

export function OverlayMenu({ label, icon, testId, children }: OverlayMenuProps): React.JSX.Element {
  const { open, setOpen, hostRef } = usePopupOpenState();
  return (
    <div css={HOST_STYLE} ref={hostRef}>
      <button
        type='button'
        css={[BUTTON_STYLE, open && BUTTON_OPEN_STYLE]}
        data-testid={testId}
        aria-label={label}
        title={label}
        aria-haspopup='dialog'
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        {icon}
      </button>
      {open && (
        <div
          css={CARD_STYLE}
          role='dialog'
          aria-label={label}
          data-testid={testId.replace(/-button$/, '-card')}
          onKeyDown={e => {
            if (e.key === 'Escape') setOpen(false);
          }}
        >
          <CloseOverlayMenuContext.Provider value={() => setOpen(false)}>{children}</CloseOverlayMenuContext.Provider>
        </div>
      )}
    </div>
  );
}
